import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import RnVerificationPanel from "@/components/RnVerificationPanel";
import CredentialCenter from "@/components/CredentialCenter";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  HeartPulse, ShieldCheck, Clock, Loader2, Upload, Check, CreditCard, FileText,
  IdCard, Camera, Briefcase, Wallet, Star, Activity, Stethoscope, Hourglass, X,
  Phone, MessageSquare, FilePlus, Download, Timer, Percent, TrendingUp, DollarSign, Plus, Send, Pencil,
} from "lucide-react";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-cobalt";

const DOCS = [
  { key: "resume", label: "Resume / CV", icon: FileText },
  { key: "license_doc", label: "Nursing license verification", icon: ShieldCheck },
  { key: "gov_id", label: "Government ID", icon: IdCard },
  { key: "profile_photo", label: "Profile photo", icon: Camera },
];

export default function NursePortal() {
  const [nurse, setNurse] = useState(undefined);
  const [dash, setDash] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/nurse/me");
      setNurse(data);
      if (data.status === "active") {
        const d = await api.get("/nurse/dashboard").catch(() => null);
        if (d) setDash(d.data);
      }
    } catch { setNurse(null); }
  };
  useEffect(() => { load(); }, []);

  if (nurse === undefined) return <AppLayout><div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div></AppLayout>;
  if (nurse === null) {
    return (
      <AppLayout>
        <div className="max-w-md mx-auto py-16 text-center bg-white rounded-3xl border border-slate-100 p-8 shadow-sm" data-testid="nurse-no-application">
          <HeartPulse className="w-10 h-10 text-cobalt mx-auto mb-3" />
          <h1 className="font-heading text-xl font-bold text-navy">No RN application yet</h1>
          <p className="text-sm text-slate-500 mt-2">Start your registered nurse application to join the Crash Ready RN Triage Network. You can save your progress and submit when ready.</p>
          <button onClick={async () => {
            try { await api.post("/nurses/apply", {}); window.location.reload(); }
            catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not start application"); }
          }} data-testid="nurse-start-application" className="mt-5 px-6 py-3 rounded-full bg-cobalt text-white font-medium hover:-translate-y-0.5 transition-all">
            Start RN application
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto" data-testid="nurse-portal">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-2xl bg-navy flex items-center justify-center"><HeartPulse className="w-6 h-6 text-cyan-300" /></div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-navy">RN Triage Network</h1>
            <p className="text-sm text-slate-500">Welcome, {nurse.first_name || nurse.name}.</p>
          </div>
        </div>
        {nurse.status === "active" ? <ActiveDashboard nurse={nurse} dash={dash} /> : <PendingView nurse={nurse} onChange={load} />}
      </div>
    </AppLayout>
  );
}

function StatusBanner({ status, submitted }) {
  const map = {
    pending: submitted
      ? { icon: Hourglass, cls: "bg-cobalt/5 text-cobalt border-cobalt/20", title: "Application submitted — under review", sub: "An administrator is reviewing your application. You can still update your details, documents and banking below." }
      : { icon: Pencil, cls: "bg-amber-50 text-amber-700 border-amber-100", title: "Complete your application", sub: "Fill in your application details below, upload your documents, then hit Submit application for review." },
    rejected: { icon: Clock, cls: "bg-red-50 text-red-700 border-red-100", title: "Application not approved", sub: "Please contact support if you believe this is an error." },
    suspended: { icon: Clock, cls: "bg-slate-100 text-slate-600 border-slate-200", title: "Account suspended", sub: "Your RN account is currently suspended." },
  }[status] || {};
  const Icon = map.icon || Clock;
  return (
    <div className={`rounded-2xl border p-4 flex items-start gap-3 ${map.cls}`} data-testid="nurse-status-banner">
      <Icon className="w-5 h-5 mt-0.5 shrink-0" />
      <div><p className="font-semibold">{map.title}</p><p className="text-sm opacity-90">{map.sub}</p></div>
    </div>
  );
}

function ApplicationForm({ nurse, onChange }) {
  const [f, setF] = useState({
    first_name: nurse.first_name || "", last_name: nurse.last_name || "",
    phone: nurse.phone || "", address: nurse.address || "",
    license_number: nurse.license_number || "",
    states_licensed: (nurse.states_licensed || []).join(", "),
    npi: nurse.npi || "", years_experience: nurse.years_experience || "",
    nursing_school: nurse.nursing_school || "", degrees: Array.isArray(nurse.degrees) ? nurse.degrees.join(", ") : (nurse.degrees || ""),
    certifications: (nurse.certifications || []).join(", "),
    trauma_experience: !!nurse.trauma_experience, ed_experience: !!nurse.ed_experience,
    case_management_experience: !!nurse.case_management_experience,
  });
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const payload = () => ({
    ...f,
    states_licensed: f.states_licensed.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
    certifications: f.certifications.split(",").map((s) => s.trim()).filter(Boolean),
  });

  const save = async () => {
    setSaving(true);
    try { await api.put("/nurse/profile", payload()); toast.success("Application saved"); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.put("/nurse/profile", payload());
      await api.post("/nurse/submit");
      toast.success("Application submitted for admin review");
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not submit"); }
    finally { setSubmitting(false); }
  };

  const T = (k, label, ph) => (
    <div>
      <label className="text-[11px] font-semibold text-slate-500 uppercase">{label}</label>
      <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`nurse-app-${k}`} className={`${inputCls} mt-1`} placeholder={ph} />
    </div>
  );
  const C = (k, label) => (
    <label className="flex items-center gap-2 text-sm text-slate-600">
      <input type="checkbox" checked={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`nurse-app-${k}`} className="accent-cobalt" /> {label}
    </label>
  );

  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm" data-testid="nurse-application-form">
      <h2 className="font-heading font-semibold text-navy mb-1 flex items-center gap-2"><Pencil className="w-4 h-4 text-cobalt" /> Application details</h2>
      <p className="text-sm text-slate-500 mb-4">Edit your application anytime before approval. Save your progress, then submit for review when everything is complete.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {T("first_name", "First name", "Jane")}{T("last_name", "Last name", "Doe")}
        {T("phone", "Phone", "+1 …")}{T("address", "Address", "Street, City, State")}
        {T("license_number", "RN license number", "RN-000000")}{T("states_licensed", "States licensed (comma-sep)", "TX, FL")}
        {T("npi", "NPI (optional)", "NPI number")}{T("years_experience", "Years of experience", "e.g. 8")}
        {T("nursing_school", "Nursing school", "School name")}{T("degrees", "Degrees", "BSN, MSN")}
        {T("certifications", "Certifications (comma-sep)", "CEN, TCRN")}
      </div>
      <div className="mt-3 flex flex-wrap gap-4">
        {C("trauma_experience", "Trauma experience")}
        {C("ed_experience", "Emergency department experience")}
        {C("case_management_experience", "Case management experience")}
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button onClick={save} disabled={saving} data-testid="nurse-app-save"
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 text-navy text-sm font-semibold hover:bg-slate-50 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save application
        </button>
        <button onClick={submit} disabled={submitting} data-testid="nurse-app-submit"
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-white text-sm font-semibold hover:-translate-y-0.5 transition-all disabled:opacity-60">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {nurse.submitted_at ? "Resubmit application" : "Submit application for review"}
        </button>
        {nurse.submitted_at && <span className="text-xs text-slate-400" data-testid="nurse-app-submitted-at">Submitted {new Date(nurse.submitted_at).toLocaleString()}</span>}
      </div>
    </div>
  );
}

function PendingView({ nurse, onChange }) {
  const uploaded = nurse.documents_uploaded || {};
  const fileRefs = useRef({});
  const [busy, setBusy] = useState("");
  const [bank, setBank] = useState({ account_holder: "", routing_number: "", account_number: "" });
  const [savingBank, setSavingBank] = useState(false);

  const upload = async (key, file) => {
    if (!file) return;
    setBusy(key);
    try {
      const fd = new FormData();
      fd.append("doc_type", key); fd.append("file", file);
      await api.post("/nurse/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(""); }
  };

  const saveBank = async () => {
    if (!bank.account_holder || !bank.routing_number || !bank.account_number) { toast.error("Fill all banking fields."); return; }
    setSavingBank(true);
    try { await api.put("/nurse/banking", bank); toast.success("Banking saved"); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSavingBank(false); }
  };

  return (
    <div className="space-y-6">
      <StatusBanner status={nurse.status} submitted={!!nurse.submitted_at} />

      {nurse.status === "pending" && <ApplicationForm nurse={nurse} onChange={onChange} />}

      <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm">
        <h2 className="font-heading font-semibold text-navy mb-1 flex items-center gap-2"><Upload className="w-4 h-4 text-cobalt" /> Verification documents</h2>
        <p className="text-sm text-slate-500 mb-4">Upload each item so an admin can verify your credentials.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {DOCS.map(({ key, label, icon: Icon }) => {
            const done = uploaded[key];
            return (
              <div key={key} className="rounded-2xl border border-slate-100 p-4 flex items-center justify-between gap-3" data-testid={`nurse-doc-${key}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${done ? "bg-emerald-50" : "bg-slate-100"}`}>
                    {done ? <Check className="w-4 h-4 text-emerald-600" /> : <Icon className="w-4 h-4 text-slate-500" />}
                  </div>
                  <span className="text-sm font-medium text-navy truncate">{label}</span>
                </div>
                <input type="file" hidden ref={(el) => (fileRefs.current[key] = el)} onChange={(e) => upload(key, e.target.files?.[0])} data-testid={`nurse-doc-input-${key}`} />
                <button onClick={() => fileRefs.current[key]?.click()} disabled={busy === key} data-testid={`nurse-doc-btn-${key}`}
                  className="shrink-0 text-xs font-semibold px-3 py-2 rounded-full border border-slate-200 text-cobalt hover:bg-cobalt-light disabled:opacity-60">
                  {busy === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : done ? "Replace" : "Upload"}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm">
        <h2 className="font-heading font-semibold text-navy mb-1 flex items-center gap-2"><CreditCard className="w-4 h-4 text-cobalt" /> Banking for payouts</h2>
        {nurse.banking_on_file ? (
          <p className="text-sm text-emerald-600 flex items-center gap-1.5 mt-2" data-testid="nurse-banking-onfile"><Check className="w-4 h-4" /> Bank account on file ending ••{nurse.banking_last4}</p>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-4">Used for your weekly intake payouts. [COMPLIANCE PLACEHOLDER — replace with a compliant payment processor before production.]</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <input className={inputCls} placeholder="Account holder" value={bank.account_holder} onChange={(e) => setBank({ ...bank, account_holder: e.target.value })} data-testid="nurse-bank-holder" />
              <input className={inputCls} placeholder="Routing number" value={bank.routing_number} onChange={(e) => setBank({ ...bank, routing_number: e.target.value })} data-testid="nurse-bank-routing" />
              <input className={inputCls} placeholder="Account number" value={bank.account_number} onChange={(e) => setBank({ ...bank, account_number: e.target.value })} data-testid="nurse-bank-account" />
            </div>
            <button onClick={saveBank} disabled={savingBank} data-testid="nurse-bank-save" className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-white text-sm font-semibold hover:-translate-y-0.5 transition-all disabled:opacity-60">
              {savingBank ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Save banking
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ActiveDashboard({ nurse, dash }) {
  const [data, setData] = useState(dash);
  const [online, setOnline] = useState(nurse.availability === "online");
  const [openIntake, setOpenIntake] = useState(null);
  const [tab, setTab] = useState("operations");

  const reload = () => api.get("/nurse/dashboard").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { if (!dash) reload(); }, []); // eslint-disable-line

  const toggle = async () => {
    const next = !online; setOnline(next);
    try { await api.post("/nurse/availability", { online: next }); toast.success(next ? "You're online" : "You're offline"); }
    catch { setOnline(!next); }
  };
  const act = async (id, action) => {
    try {
      await api.post(`/nurse/intakes/${id}/${action}`);
      toast.success(action === "accept" ? "Case accepted" : "Declined");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || (e?.response?.status === 409 ? "Nurse already assigned" : "Failed"));
      reload();
    }
  };

  const m = data?.metrics || {};
  const stat = (label, value, Icon) => (
    <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm" data-testid={`nurse-stat-${label.toLowerCase().replace(/ /g, '-')}`}>
      <div className="flex items-center gap-2 text-slate-400 text-xs font-medium"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="mt-1 font-heading text-2xl font-bold text-navy">{value}</div>
    </div>
  );
  const scoreBadge = (s) => s == null ? null : <span className="text-xs font-bold text-cobalt bg-cobalt-light px-2 py-0.5 rounded-full">Score {s}</span>;

  const Card = ({ c, children }) => (
    <div className="rounded-2xl border border-slate-100 p-4 bg-slate-50/60" data-testid={`intake-card-${c.intake_id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-navy text-sm truncate">{c.patient_name || "Patient"}</div>
          <div className="text-xs text-slate-500">{c.case_number} · {c.accident_time ? new Date(c.accident_time).toLocaleDateString() : ""}{c.damage_location ? ` · ${c.damage_location}` : ""}</div>
        </div>
        {scoreBadge(c.crash_ready_score)}
      </div>
      <div className="mt-3 flex gap-2">{children}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="nurse-dashboard">
      <div className="rounded-2xl bg-white border border-slate-100 p-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-2 text-sm"><span className={`w-2.5 h-2.5 rounded-full ${online ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} /> <span className="font-medium text-navy">{online ? "Online — receiving intakes" : "Offline"}</span></div>
        <button onClick={toggle} data-testid="nurse-availability-toggle" className={`w-12 h-6 rounded-full transition-colors relative ${online ? "bg-emerald-500" : "bg-slate-300"}`}>
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${online ? "translate-x-6" : "translate-x-0.5"}`} />
        </button>
      </div>
      <div className="flex items-center gap-2 border-b border-slate-100">
        <button onClick={() => setTab("operations")} data-testid="nurse-tab-operations" className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "operations" ? "border-cobalt text-cobalt" : "border-transparent text-slate-400 hover:text-navy"}`}>Operations</button>
        <button onClick={() => setTab("earnings")} data-testid="nurse-tab-earnings" className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "earnings" ? "border-cobalt text-cobalt" : "border-transparent text-slate-400 hover:text-navy"}`}>Earnings & Payouts</button>
        <button onClick={() => setTab("credentials")} data-testid="nurse-tab-credentials" className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "credentials" ? "border-cobalt text-cobalt" : "border-transparent text-slate-400 hover:text-navy"}`}>Credentials</button>
      </div>

      {tab === "earnings" ? <EarningsTab /> : tab === "credentials" ? <CredentialCenter role="nurse" title="RN Credential Center" /> : (
      <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stat("Intakes Completed", m.intakes_completed ?? 0, Stethoscope)}
        {stat("Weekly Earnings", `$${(m.weekly_earnings ?? 0).toFixed(0)}`, Wallet)}
        {stat("Pending Payouts", `$${(m.pending_payouts ?? 0).toFixed(0)}`, Clock)}
        {stat("Completion", `${m.completion_pct ?? 0}%`, Percent)}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stat("Avg Response", m.avg_response_time_min != null ? `${m.avg_response_time_min}m` : "—", Timer)}
        {stat("Avg Intake", m.avg_intake_duration_min != null ? `${m.avg_intake_duration_min}m` : "—", Activity)}
        {stat("Total Calls", m.total_calls ?? 0, Phone)}
        {stat("Quality", m.quality_score ?? "—", TrendingUp)}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <h3 className="font-heading font-semibold text-navy flex items-center gap-2 mb-3"><Activity className="w-4 h-4 text-cobalt" /> Available <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{data?.available_cases?.length || 0}</span></h3>
          <div className="space-y-2">
            {(data?.available_cases || []).length === 0 ? <p className="text-sm text-slate-400">No new intakes right now.</p> :
              (data.available_cases).map((c) => (
                <Card key={c.intake_id} c={c}>
                  <button onClick={() => act(c.intake_id, "accept")} data-testid={`intake-accept-${c.intake_id}`} className="flex-1 py-2 rounded-full bg-cobalt text-white text-xs font-semibold hover:bg-cobalt-hover">Accept</button>
                  <button onClick={() => act(c.intake_id, "decline")} data-testid={`intake-decline-${c.intake_id}`} className="px-3 py-2 rounded-full border border-slate-200 text-slate-500 text-xs hover:bg-slate-50">Decline</button>
                </Card>
              ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <h3 className="font-heading font-semibold text-navy flex items-center gap-2 mb-3"><Briefcase className="w-4 h-4 text-cobalt" /> Accepted <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{data?.accepted_cases?.length || 0}</span></h3>
          <div className="space-y-2">
            {(data?.accepted_cases || []).length === 0 ? <p className="text-sm text-slate-400">No active intakes.</p> :
              (data.accepted_cases).map((c) => (
                <Card key={c.intake_id} c={c}>
                  <button onClick={() => setOpenIntake(c.intake_id)} data-testid={`intake-open-${c.intake_id}`} className="flex-1 py-2 rounded-full bg-navy text-white text-xs font-semibold hover:bg-navy/90">{c.status === "in_progress" ? "Continue intake" : "Start intake"}</button>
                </Card>
              ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <h3 className="font-heading font-semibold text-navy flex items-center gap-2 mb-3"><Check className="w-4 h-4 text-cobalt" /> Completed <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{data?.completed_cases?.length || 0}</span></h3>
          <div className="space-y-2">
            {(data?.completed_cases || []).length === 0 ? <p className="text-sm text-slate-400">No completed intakes yet.</p> :
              (data.completed_cases).map((c) => (
                <Card key={c.intake_id} c={c}>
                  <button onClick={() => setOpenIntake(c.intake_id)} data-testid={`intake-view-${c.intake_id}`} className="flex-1 py-2 rounded-full border border-slate-200 text-navy text-xs font-semibold hover:bg-slate-50">View intake</button>
                </Card>
              ))}
          </div>
        </div>
      </div>

      {openIntake && <RnIntakeWorkflow intakeId={openIntake} onClose={() => setOpenIntake(null)} onDone={() => { setOpenIntake(null); reload(); }} />}
      </>
      )}
    </div>
  );
}

function EarningsTab() {
  const [e, setE] = useState(null);
  const load = () => api.get("/nurse/earnings").then(({ data }) => setE(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!e) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  const dl = async (fmt) => {
    try {
      const res = await api.get(`/nurse/earnings/statement?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `rn_earnings.${fmt}`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const card = (label, value, Icon, cls = "text-navy") => (
    <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm" data-testid={`earn-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center gap-2 text-slate-400 text-xs font-medium"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className={`mt-1 font-heading text-2xl font-bold ${cls}`}>{value}</div>
    </div>
  );
  return (
    <div className="space-y-5" data-testid="nurse-earnings">
      <div className="rounded-2xl bg-amber-50 border border-amber-100 p-3 text-xs text-amber-700">Payouts run weekly. Money movement is SANDBOX until live payment processing is enabled. Rate ${Number(e.payout_rate || 0).toFixed(0)}/completed intake.</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {card("Lifetime", money(e.lifetime_earnings), DollarSign)}
        {card("Pending", money(e.pending_payouts), Clock, "text-amber-600")}
        {card("Paid", money(e.paid_payouts), Check, "text-emerald-600")}
        {card("This Week", money(e.weekly_earnings), Wallet)}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><Wallet className="w-4 h-4 text-cobalt" /> Earnings ledger</h3>
          <div className="flex gap-2">
            <button onClick={() => dl("csv")} data-testid="earn-download-csv" className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 text-cobalt hover:bg-cobalt-light"><Download className="w-3.5 h-3.5" /> CSV</button>
            <button onClick={() => dl("pdf")} data-testid="earn-download-pdf" className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 text-cobalt hover:bg-cobalt-light"><Download className="w-3.5 h-3.5" /> PDF</button>
          </div>
        </div>
        {e.entries?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="earn-ledger-table">
              <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                <th className="py-2">Case</th><th className="py-2">Amount</th><th className="py-2">Status</th><th className="py-2">Earned</th></tr></thead>
              <tbody>
                {e.entries.map((r) => (
                  <tr key={r.earning_id} className="border-b border-slate-50 last:border-0">
                    <td className="py-2 font-mono text-xs text-navy">{r.case_number || r.intake_id}</td>
                    <td className="py-2 font-semibold text-navy">{money(r.amount)}</td>
                    <td className="py-2"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.status === "paid" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{r.status}</span></td>
                    <td className="py-2 text-slate-500 text-xs">{r.earned_at ? new Date(r.earned_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-slate-400">No earnings yet. Complete an intake to earn your first payout.</p>}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <h3 className="font-heading font-semibold text-navy mb-3">Payout history</h3>
          {e.payouts?.length ? e.payouts.map((p) => (
            <div key={p.payout_id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0 text-sm" data-testid={`payout-${p.payout_id}`}>
              <div><div className="font-semibold text-navy">{money(p.amount)} · {p.count} intake(s)</div><div className="text-xs text-slate-400">{p.period} · {p.paid_at ? new Date(p.paid_at).toLocaleDateString() : ""}</div></div>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">{p.status}</span>
            </div>
          )) : <p className="text-sm text-slate-400">No payouts processed yet.</p>}
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <h3 className="font-heading font-semibold text-navy mb-3">1099 tax history</h3>
          {e.tax_1099?.length ? e.tax_1099.map((t) => (
            <div key={t.year} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0 text-sm">
              <div className="font-semibold text-navy">{t.year}</div>
              <div className="flex items-center gap-2"><span className="text-navy">{money(t.total_paid)}</span>{t.reportable_1099 && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-cobalt-light text-cobalt">1099</span>}</div>
            </div>
          )) : <p className="text-sm text-slate-400">No paid earnings yet.</p>}
        </div>
      </div>
    </div>
  );
}

function RnIntakeWorkflow({ intakeId, onClose, onDone }) {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [done, setDone] = useState([]);
  const [summary, setSummary] = useState("");
  const [recs, setRecs] = useState("");
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/nurse/intakes/${intakeId}`).then(({ data }) => {
      setD(data);
      setForm(data.intake?.intake_form || {});
      setDone(data.intake?.completed_sections || []);
      setSummary(data.intake?.summary || "");
      setRecs(data.intake?.recommendations || "");
      setSig(data.intake?.signature || "");
    }).catch(() => {});
  }, [intakeId]);

  if (!d) return null;
  const completed = d.intake?.status === "completed";
  const setItem = (sk, ik, v) => setForm({ ...form, [sk]: { ...(form[sk] || {}), [ik]: v } });
  const saveSection = async (sk) => {
    try { await api.put(`/nurse/intakes/${intakeId}/form`, { section: sk, data: form[sk] || {}, completed: true }); setDone([...new Set([...done, sk])]); toast.success("Section saved"); }
    catch { toast.error("Save failed"); }
  };
  const complete = async () => {
    if (!sig.trim()) { toast.error("Please sign to complete."); return; }
    setBusy(true);
    try { await api.post(`/nurse/intakes/${intakeId}/complete`, { summary, recommendations: recs, signature: sig.trim() }); toast.success("Intake completed — attorney notified"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[95] bg-navy/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="rn-intake-workflow">
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div><h3 className="font-heading font-bold text-navy">Nurse Intake — {d.patient?.name}</h3>
            <p className="text-xs text-slate-500">{d.case?.case_number} · Score {d.case?.scores?.overall?.score ?? "—"}</p></div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          {d.attorney && (
            <div className="rounded-2xl bg-navy text-white p-4 flex items-center justify-between gap-3" data-testid="intake-attorney-panel">
              <div><div className="text-xs text-cyan-300 uppercase tracking-wider">Assigned attorney</div>
                <div className="font-semibold">{d.attorney.name}{d.attorney.firm_name ? ` · ${d.attorney.firm_name}` : ""}</div></div>
              <div className="flex gap-2">
                {d.attorney.phone && <a href={`tel:${d.attorney.phone}`} data-testid="intake-call-attorney" className="px-3 py-2 rounded-full bg-cyan-400 text-navy text-xs font-bold">Call</a>}
                <a href="/messages" data-testid="intake-message-attorney" className="px-3 py-2 rounded-full bg-white/10 text-white text-xs font-semibold">Message</a>
              </div>
            </div>
          )}
          <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-xs text-amber-700">[COMPLIANCE PLACEHOLDER] Documentation of patient-reported information only — not a medical diagnosis or legal determination.</div>

          <ActivityPanel intakeId={intakeId} />

          {completed ? (
            <div className="space-y-3" data-testid="intake-completed-view">
              <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 text-emerald-700 flex items-center gap-2"><Check className="w-5 h-5" /> Intake completed & signed by {d.intake.signature}.</div>
              {d.intake.summary && <div><p className="text-xs font-semibold text-slate-400 uppercase">Summary</p><p className="text-sm text-navy">{d.intake.summary}</p></div>}
              {d.intake.recommendations && <div><p className="text-xs font-semibold text-slate-400 uppercase">Recommendations</p><p className="text-sm text-navy">{d.intake.recommendations}</p></div>}
            </div>
          ) : (
            <>
              {(d.sections || []).map((s) => (
                <div key={s.key} className="rounded-2xl border border-slate-100 p-4" data-testid={`intake-section-${s.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-navy text-sm flex items-center gap-2">{done.includes(s.key) && <Check className="w-4 h-4 text-emerald-600" />}{s.title}</span>
                    <button onClick={() => saveSection(s.key)} data-testid={`intake-save-${s.key}`} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 text-cobalt hover:bg-cobalt-light">Mark done</button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {s.items.map((it) => (
                      <div key={it.key}>
                        {it.type === "bool" ? (
                          <label className="flex items-center gap-2 text-sm text-slate-600 py-1.5"><input type="checkbox" checked={!!(form[s.key] || {})[it.key]} onChange={(e) => setItem(s.key, it.key, e.target.checked)} data-testid={`intake-${s.key}-${it.key}`} className="h-4 w-4 accent-cobalt" /> {it.label}</label>
                        ) : (
                          <label className="block text-xs text-slate-500">{it.label}
                            <input type={it.type === "number" ? "number" : "text"} value={(form[s.key] || {})[it.key] || ""} onChange={(e) => setItem(s.key, it.key, e.target.value)} data-testid={`intake-${s.key}-${it.key}`} className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 text-sm outline-none focus:border-cobalt" /></label>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="rounded-2xl border border-slate-100 p-4 space-y-3">
                <label className="block text-sm font-medium text-navy">Summary notes
                  <textarea value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="intake-summary" rows={3} className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-cobalt" /></label>
                <label className="block text-sm font-medium text-navy">Recommendations
                  <textarea value={recs} onChange={(e) => setRecs(e.target.value)} data-testid="intake-recommendations" rows={2} className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-cobalt" /></label>
                <label className="block text-sm font-medium text-navy">Electronic signature
                  <input value={sig} onChange={(e) => setSig(e.target.value)} data-testid="intake-signature" placeholder="Type your full name, RN" className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-cobalt" /></label>
              </div>
            </>
          )}

          <RnVerificationPanel intakeId={intakeId} />
        </div>
        {!completed && (
          <div className="p-5 border-t border-slate-100 flex justify-end">
            <button onClick={complete} disabled={busy} data-testid="intake-complete-btn" className="flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-white text-sm font-semibold hover:-translate-y-0.5 transition-all disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Sign &amp; complete intake
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function fmtSec(s) {
  s = Math.round(s || 0);
  const m = Math.floor(s / 60), r = s % 60;
  return m ? `${m}m ${r}s` : `${r}s`;
}

function ActivityPanel({ intakeId }) {
  const [sum, setSum] = useState(null);
  const [callActive, setCallActive] = useState(false);
  const callStart = useRef(null);
  const [tick, setTick] = useState(0);
  const [note, setNote] = useState("");
  const [logType, setLogType] = useState("message");

  const load = () => api.get(`/nurse/intakes/${intakeId}/activity`).then(({ data }) => setSum(data)).catch(() => {});
  useEffect(() => { load(); }, [intakeId]); // eslint-disable-line
  useEffect(() => { if (!callActive) return; const t = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(t); }, [callActive]);

  const post = async (payload) => {
    try { await api.post(`/nurse/intakes/${intakeId}/activity`, payload); load(); return true; }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to log"); return false; }
  };

  const startCall = () => { callStart.current = new Date(); setCallActive(true); setTick(0); };
  const endCall = async () => {
    const start = callStart.current, end = new Date();
    setCallActive(false);
    const ok = await post({ type: "call", started_at: start.toISOString(), ended_at: end.toISOString(),
      duration_sec: Math.round((end - start) / 1000), outcome: "completed", notes: note || "Patient call" });
    if (ok) { toast.success("Call logged"); setNote(""); }
  };
  const logEntry = async () => {
    if (!note.trim()) { toast.error("Add a note first"); return; }
    const ok = await post({ type: logType, notes: note.trim(), outcome: logType });
    if (ok) { toast.success("Logged"); setNote(""); }
  };

  const s = sum || {};
  const ICONS = { call: Phone, message: MessageSquare, document_request: FilePlus, note: FileText, section_completed: Check, form_signed: Check, completed: Check, checklist_item: Check, recording: Activity };
  return (
    <div className="rounded-2xl border border-slate-100 p-4" data-testid="rn-activity-panel">
      <h4 className="font-semibold text-navy text-sm flex items-center gap-2 mb-3"><Phone className="w-4 h-4 text-cobalt" /> Activity & Call Tracking</h4>
      <div className="grid grid-cols-4 gap-2 mb-3 text-center">
        <div className="rounded-xl bg-slate-50 p-2"><div className="text-[10px] text-slate-400 uppercase">Calls</div><div className="font-bold text-navy" data-testid="rn-activity-calls">{s.total_calls ?? 0}</div></div>
        <div className="rounded-xl bg-slate-50 p-2"><div className="text-[10px] text-slate-400 uppercase">Call time</div><div className="font-bold text-navy">{fmtSec(s.total_call_time_sec)}</div></div>
        <div className="rounded-xl bg-slate-50 p-2"><div className="text-[10px] text-slate-400 uppercase">Total time</div><div className="font-bold text-navy">{fmtSec(s.total_time_sec)}</div></div>
        <div className="rounded-xl bg-slate-50 p-2"><div className="text-[10px] text-slate-400 uppercase">Activities</div><div className="font-bold text-navy">{s.total_activities ?? 0}</div></div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {callActive ? (
          <button onClick={endCall} data-testid="rn-call-end" className="flex items-center gap-2 px-4 py-2 rounded-full bg-red-500 text-white text-xs font-semibold animate-pulse"><Timer className="w-3.5 h-3.5" /> End call · {fmtSec(tick)}</button>
        ) : (
          <button onClick={startCall} data-testid="rn-call-start" className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 text-white text-xs font-semibold"><Phone className="w-3.5 h-3.5" /> Start call</button>
        )}
        <select value={logType} onChange={(e) => setLogType(e.target.value)} data-testid="rn-activity-type" className="text-xs bg-slate-50 border border-slate-200 rounded-full px-3 py-2 outline-none">
          <option value="message">Message</option>
          <option value="document_request">Document request</option>
          <option value="note">Note</option>
        </select>
        <button onClick={logEntry} data-testid="rn-activity-log" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-navy text-white text-xs font-semibold"><Plus className="w-3.5 h-3.5" /> Log</button>
      </div>
      <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="rn-activity-note" placeholder={callActive ? "Call notes (optional)…" : "Notes for message / request / note…"}
        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-cobalt mb-3" />

      <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="rn-activity-list">
        {(s.activities || []).length === 0 ? <p className="text-xs text-slate-400">No activity logged yet.</p> :
          [...(s.activities || [])].reverse().map((a) => {
            const Icon = ICONS[a.type] || Activity;
            return (
              <div key={a.activity_id} className="flex items-center gap-2 text-xs text-slate-600 bg-slate-50 rounded-lg px-2.5 py-1.5">
                <Icon className="w-3.5 h-3.5 text-cobalt shrink-0" />
                <span className="font-medium capitalize">{(a.type || "").replace(/_/g, " ")}</span>
                {a.duration_sec ? <span className="text-slate-400">· {fmtSec(a.duration_sec)}</span> : null}
                {a.notes ? <span className="text-slate-400 truncate">· {a.notes}</span> : null}
                <span className="ml-auto text-slate-300">{a.created_at ? new Date(a.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</span>
              </div>
            );
          })}
      </div>
    </div>
  );
}
